<template>
  <div class="col-xl-12 xl-100 box-col-6">
    <div class="card">
      <div class="card-header">
        <h5>
          Fare Configuration
        </h5>
      </div>

      <div class="card-body" v-if="fareConfig.state">

        <div class="row mb-3">
          <div class="col-sm-4 d-flex align-items-end">
            <button class="btn btn-primary pull-right" @click="saveConfiguration">
              Save Configuration
            </button>
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-sm-4">
            <label class="form-label">Currency:</label>
            <select v-model="fareConfig.state.currency" class="form-select">
              <option value="CAD">CAD</option>
            </select>
          </div>
          <div class="col-sm-4">
            <label for="fare-config-platform-fee" class="form-label">Platform Fee
              ({{ fareConfig.state.currency }})</label>
            <div class="input-group">
              <span class="input-group-text">$</span>
              <input type="number" class="form-control" id="fare-config-platform-fee"
                     v-model="fareConfig.state.platformFee" min="0" step="0.01" required>
            </div>
            <small class="text-muted">Flat part of the fee charged per ride (excluded from driver payout)</small>
            <div id="fare-config-platform-fee-error" class="invalid-feedback d-none">
              Please enter a valid platform fee (0 or more).
            </div>
          </div>
          <div class="col-sm-4">
            <label for="fare-config-platform-fee-per-km" class="form-label">Platform Fee Per Km
              ({{ fareConfig.state.currency }})</label>
            <div class="input-group">
              <span class="input-group-text">$</span>
              <input type="number" class="form-control" id="fare-config-platform-fee-per-km"
                     v-model="fareConfig.state.platformFeePerKm" min="0" step="0.01">
            </div>
            <small class="text-muted">Distance-scaled part of the fee, charged on the ride's own distance</small>
            <small v-if="fareConfig.isPlatformFeePerKmBelowFloor()"
                   class="d-block text-warning">
              Below the ${{ platformFeePerKmFloor.toFixed(2) }}/km floor — margin shrinks as trips get
              longer. Move the fare, not the fee.
            </small>
            <div id="fare-config-platform-fee-per-km-error" class="invalid-feedback d-none">
              Please enter a valid per-km platform fee (0 or more).
            </div>
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-sm-4">
            <label for="fare-config-platform-fee-minimum" class="form-label">Platform Fee Minimum
              ({{ fareConfig.state.currency }})</label>
            <div class="input-group">
              <span class="input-group-text">$</span>
              <input type="number" class="form-control" id="fare-config-platform-fee-minimum"
                     v-model="fareConfig.state.platformFeeMinimum" min="0" step="0.01">
            </div>
            <small class="text-muted">Floor on the whole fee, flat plus per-km (0 = no floor)</small>
            <div id="fare-config-platform-fee-minimum-error" class="invalid-feedback d-none">
              Please enter a valid platform fee minimum (0 or more).
            </div>
          </div>
          <div class="col-sm-4">
            <label for="fare-config-platform-fee-maximum" class="form-label">Platform Fee Maximum
              ({{ fareConfig.state.currency }})</label>
            <div class="input-group">
              <span class="input-group-text">$</span>
              <input type="number" class="form-control" id="fare-config-platform-fee-maximum"
                     v-model="fareConfig.state.platformFeeMaximum" min="0" step="0.01">
            </div>
            <small class="text-muted">Cap on the whole fee (blank or 0 = uncapped)</small>
            <div id="fare-config-platform-fee-maximum-error" class="invalid-feedback d-none">
              Platform fee maximum must be 0 or more, and cannot be below the minimum.
            </div>
          </div>
        </div>

        <div class="row mb-4">
          <div class="col-sm-4">
            <label for="fare-config-scheduled-ride-fee" class="form-label">Scheduled Ride Fee
              ({{ fareConfig.state.currency }})</label>
            <div class="input-group">
              <span class="input-group-text">$</span>
              <input type="number" class="form-control" id="fare-config-scheduled-ride-fee"
                     v-model="fareConfig.state.scheduledRideFee" min="0" step="0.01" required>
            </div>
            <small class="text-muted">Additional fee applied to pre-scheduled bookings</small>
            <div id="fare-config-scheduled-ride-fee-error" class="invalid-feedback d-none">
              Please enter a valid scheduled ride fee (0 or more).
            </div>
          </div>
          <div class="col-sm-4">
            <label for="fare-config-per-minute-fare" class="form-label">Per Minute Fare
              ({{ fareConfig.state.currency }})</label>
            <div class="input-group">
              <span class="input-group-text">$</span>
              <input type="number" class="form-control" id="fare-config-per-minute-fare"
                     v-model="fareConfig.state.perMinuteFare" min="0" step="0.01" required>
            </div>
            <small class="text-muted">Fare charged per minute of ride duration</small>
            <div id="fare-config-per-minute-fare-error" class="invalid-feedback d-none">
              Please enter a valid per-minute fare (0 or more).
            </div>
          </div>
          <div class="col-sm-4">
            <label for="fare-config-minimum-fare" class="form-label">Minimum Fare
              ({{ fareConfig.state.currency }})</label>
            <div class="input-group">
              <span class="input-group-text">$</span>
              <input type="number" class="form-control" id="fare-config-minimum-fare"
                     v-model="fareConfig.state.minimumFare" min="0" step="0.01">
            </div>
            <small class="text-muted">Lowest fare charged per ride (0 = no minimum)</small>
            <div id="fare-config-minimum-fare-error" class="invalid-feedback d-none">
              Please enter a valid minimum fare (0 or more).
            </div>
            <div class="form-check form-switch mt-2">
              <input class="form-check-input" type="checkbox" role="switch"
                     id="fare-config-minimum-applies-to-fare-only"
                     v-model="fareConfig.state.minimumAppliesToFareOnly">
              <label class="form-check-label" for="fare-config-minimum-applies-to-fare-only">
                Minimum applies to the fare only
              </label>
            </div>
            <small class="text-muted d-block">On: a floored ride is charged the minimum plus the
              platform fee, and the driver's floor is the whole minimum. Off: the minimum is the
              all-in total charged and the platform fee comes out of it.</small>
          </div>
        </div>

        <div class="accordion dark-accordion" id="outlineaccordion">

          <!-- Ride Stop Config Section Header -->
          <div class="accordion-item accordion-wrapper accordion-left-border">
            <h2 class="accordion-header" id="rideStopConfigHeader">
              <button class="accordion-button collapsed"
                      type="button" data-bs-toggle="collapse"
                      data-bs-target="#ride-stop-config-collapse"
                      aria-expanded="false"
                      aria-controls="ride-stop-config-collapse">
                <span>
                  <i class="fa-solid fa-clock"></i>
                  Ride Stop Config
                </span>

                <i class="iconly-Arrow-Down-2 icli ms-auto icon"></i>
              </button>
            </h2>
          </div>

          <!-- Base Prices Section Header -->
          <div class="accordion-item accordion-wrapper accordion-left-border">
            <h2 class="accordion-header" id="basePricesHeader">
              <button class="accordion-button collapsed"
                      type="button" data-bs-toggle="collapse"
                      data-bs-target="#base-prices-collapse"
                      aria-expanded="false"
                      aria-controls="base-prices-collapse">
                <span>
                  <i class="fa-solid fa-dollar-sign"></i>
                  Vehicle Base Fare Configurations
                </span>

                <i class="iconly-Arrow-Down-2 icli ms-auto icon"></i>
              </button>
            </h2>
          </div>

          <!-- Distance Ranges Section Header -->
          <div class="accordion-item accordion-wrapper accordion-left-border">
            <h2 class="accordion-header" id="distancePricingsHeader">
              <button class="accordion-button collapsed"
                      type="button" data-bs-toggle="collapse"
                      data-bs-target="#distance-ranges-collapse"
                      aria-expanded="false"
                      aria-controls="distance-ranges-collapse">
                <span>
                  <i class="fa-solid fa-ruler-horizontal"></i>
                  Distance Fare Configurations
                </span>

                <i class="iconly-Arrow-Down-2 icli ms-auto icon"></i>
              </button>
            </h2>
          </div>

          <!-- Geographic Zones Section Header -->
          <div class="accordion-item accordion-wrapper accordion-left-border">
            <h2 class="accordion-header" id="geoZonesPricingHeader">
              <button class="accordion-button collapsed"
                      type="button" data-bs-toggle="collapse"
                      data-bs-target="#geo-zones-collapse"
                      aria-expanded="false"
                      aria-controls="geo-zones-collapse">
                <span>
                  <i class="fa-solid fa-map"></i>
                  Geographic Zones Fare Configurations
                </span>

                <i class="iconly-Arrow-Down-2 icli ms-auto icon"></i>
              </button>
            </h2>
          </div>
        </div>

        <!-- Accordion Bodies Container -->
        <div class="accordion-bodies-container mt-4" id="accordion-bodies-container">

          <!-- Ride Stop Config Body -->
          <div class="accordion-collapse collapse" id="ride-stop-config-collapse"
               aria-labelledby="rideStopConfigHeader" data-bs-parent="#accordion-bodies-container">
            <div class="accordion-body">
              <div class="row">
                <div class="col-md-4 mb-3">
                  <label for="fare-config-per-stop-charge" class="form-label">Per Stop Charge
                    ({{ fareConfig.state.currency }})</label>
                  <div class="input-group">
                    <span class="input-group-text">$</span>
                    <input type="number" class="form-control" id="fare-config-per-stop-charge"
                           v-model="fareConfig.state.perStopCharge" min="0" step="0.01" required>
                  </div>
                  <div id="fare-config-per-stop-charge-error" class="invalid-feedback d-none">
                    Please enter a valid per stop charge (0 or more).
                  </div>
                </div>
                <div class="col-md-4 mb-3">
                  <label for="fare-config-free-waiting-minutes" class="form-label">Free Waiting Minutes</label>
                  <input type="number" class="form-control" id="fare-config-free-waiting-minutes"
                         v-model="fareConfig.state.freeStopWaitingMinutes" min="0" required>
                  <div id="fare-config-free-waiting-minutes-error" class="invalid-feedback d-none">
                    Please enter a valid number of free waiting minutes (0 or more).
                  </div>
                </div>
                <div class="col-md-4 mb-3">
                  <label for="fare-config-per-minute-charge" class="form-label">Per Minute Charge
                    ({{ fareConfig.state.currency }})</label>
                  <div class="input-group">
                    <span class="input-group-text">$</span>
                    <input type="number" class="form-control" id="fare-config-per-minute-charge"
                           v-model="fareConfig.state.stopPerMinuteCharge" min="0" step="0.01" required>
                  </div>
                  <div id="fare-config-per-minute-charge-error" class="invalid-feedback d-none">
                    Please enter a valid per minute charge (0 or more).
                  </div>
                </div>
              </div>

              <!-- Per Passenger Pricing -->
              <div class="mt-4">
                <h6 class="mb-3">Per Passenger Pricing</h6>
                <div class="row">
                  <div class="col-md-4 mb-3">
                    <label for="fare-config-per-additional-passenger-charge" class="form-label">
                      Per Additional Passenger ({{ fareConfig.state.currency }})
                    </label>
                    <div class="input-group">
                      <span class="input-group-text">$</span>
                      <input type="number" class="form-control" id="fare-config-per-additional-passenger-charge"
                             v-model="fareConfig.state.perAdditionalPassengerCharge" min="0" step="0.01">
                    </div>
                    <small class="text-muted">Surcharge per eligible passenger (0 = disabled)</small>
                    <div id="fare-config-per-additional-passenger-charge-error" class="invalid-feedback d-none">
                      Please enter a valid charge (0 or more).
                    </div>
                  </div>
                  <div class="col-md-4 mb-3">
                    <label for="fare-config-additional-passenger-max-charge" class="form-label">
                      Max Passenger Charge ({{ fareConfig.state.currency }})
                    </label>
                    <div class="input-group">
                      <span class="input-group-text">$</span>
                      <input type="number" class="form-control" id="fare-config-additional-passenger-max-charge"
                             v-model="fareConfig.state.additionalPassengerMaxCharge" min="0" step="0.01">
                    </div>
                    <small class="text-muted">Maximum total passenger surcharge (0 = uncapped)</small>
                    <div id="fare-config-additional-passenger-max-charge-error" class="invalid-feedback d-none">
                      Please enter a valid max charge (0 or more).
                    </div>
                  </div>
                  <div class="col-md-4 mb-3">
                    <label for="fare-config-additional-passenger-free-under-age" class="form-label">
                      Free Under Age
                    </label>
                    <input type="number" class="form-control" id="fare-config-additional-passenger-free-under-age"
                           v-model="fareConfig.state.additionalPassengerFreeUnderAge" min="0" step="1"
                           placeholder="e.g. 12">
                    <small class="text-muted">Passengers under this age are exempt (leave blank to charge all)</small>
                    <div id="fare-config-additional-passenger-free-under-age-error" class="invalid-feedback d-none">
                      Please enter a valid whole number age (0 or more).
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <!-- Base Prices Body -->
          <div class="accordion-collapse collapse" id="base-prices-collapse"
               aria-labelledby="basePricesHeader" data-bs-parent="#accordion-bodies-container">
            <div class="accordion-body">
              <div class="col-sm-3 d-md-block mb-4">
                <a class="btn btn-outline-primary align-items-center" @click="toggleCreateBasePriceModal(false)"
                   type="button">
                  <i class="fa-regular fa-square-plus"></i>
                  Add Vehicle Base Fare Config
                </a>
              </div>
              <DataTable :data="fareConfig.state.vehicleBaseFareConfigs" :columns="basePricesColumns"
                         class="table-responsive display"/>
            </div>
          </div>

          <!-- Distance Ranges Body -->
          <div class="accordion-collapse collapse" id="distance-ranges-collapse"
               aria-labelledby="distancePricingsHeader" data-bs-parent="#accordion-bodies-container">
            <div class="accordion-body">
              <div class="col-sm-3 d-md-block mb-4">
                <a class="btn btn-outline-primary align-items-center" @click="toggleDistancePricingModal(false)"
                   type="button">
                  <i class="fa-regular fa-square-plus"></i>
                  Add Distance Fare Config
                </a>
              </div>
              <DataTable :data="fareConfig.state.distanceFareConfigs" :columns="distancePricingsColumns"
                         class="table-responsive display"/>
            </div>
          </div>

          <!-- Geographic Zones Body -->
          <div class="accordion-collapse collapse" id="geo-zones-collapse"
               aria-labelledby="geoZonesPricingHeader" data-bs-parent="#accordion-bodies-container">
            <div class="accordion-body">
              <div class="col-sm-3 d-md-block mb-4">
                <a class="btn btn-outline-primary align-items-center" @click="toggleGeoZoneModal(false)" type="button">
                  <i class="fa-regular fa-square-plus"></i>
                  Add Geo Zone Fare Config
                </a>
              </div>
              <DataTable :data="fareConfig.state.geoZoneFareConfigs" :columns="geoZonesPricingColumns"
                         class="table-responsive table-border-horizontal display"/>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>

  <!-- Base Price Modal -->
  <div class="modal fade show" id="createBasePricingModal" tabindex="-1" role="dialog"
       aria-labelledby="createBasePricingModal" aria-modal="true" style="display: none;">
    <div class="modal-dialog modal-dialog-centered modal-xl" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h3 class="modal-title fs-5">{{ basePriceModalEditMode ? 'Edit' : 'Add' }} Base Price</h3>
          <button class="btn-close" type="button" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body" v-if="showBasePriceModal">
          <form>
            <div class="mb-3">
              <label for="vehicleType" class="form-label">Vehicle Type</label>
              <select id="vehicleType" class="form-select" v-model="currentBasePrice.vehicleType" required>
                <option value="">Select Vehicle Type</option>
                <option value="ALL">All</option>
                <option value="SEDAN">Sedan</option>
                <option value="SUV">SUV</option>
                <option value="MINIVAN">Minivan</option>
              </select>
              <div class="invalid-feedback">Please select a vehicle type.</div>
            </div>

            <div class="row mb-3">
              <div class="col-md-4">
                <label for="baseFare" class="form-label">Base Fare</label>
                <div class="input-group">
                  <span class="input-group-text">$</span>
                  <input type="number" class="form-control" id="baseFare" v-model="currentBasePrice.baseFare" min="0"
                         step="0.01" required>
                </div>
                <div class="invalid-feedback">Please enter a valid base fare.</div>
              </div>
            </div>

          </form>
        </div>
        <div class="modal-footer">
          <button class="btn btn-danger" type="button" data-bs-dismiss="modal">Close</button>
          <button class="btn btn-primary" type="button" @click="saveBasePrice">
            {{ basePriceModalEditMode ? 'Update' : 'Add' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Distance Range Modal -->
  <div class="modal fade show" id="distancePricingModal" tabindex="-1" role="dialog"
       aria-labelledby="distancePricingModal" aria-modal="true" style="display: none;">
    <div class="modal-dialog modal-dialog-centered modal-xl" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h3 class="modal-title fs-5">{{ distancePricingModalEditMode ? 'Update' : 'Add' }} Distance Range</h3>
          <button class="btn-close" type="button" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body" v-if="showDistancePricingModal">
          <form>
            <div class="row mb-3">
              <div class="col-md-6">
                <label for="startDistanceMeters" class="form-label">Start Distance (meters)</label>
                <input type="number" class="form-control" id="startDistanceMeters"
                       v-model="currentDistancePricing.startDistanceMeters" min="0" required
                       :disabled="currentDistancePricing.isStartingRange">
                <small class="text-muted">{{ (currentDistancePricing.startDistanceMeters / 1000).toFixed(2) }}
                  km</small>
                <div class="invalid-feedback">Please enter a valid start distance.</div>
              </div>
              <div class="col-md-6">
                <label for="endDistanceMeters" class="form-label">End Distance (meters)</label>
                <input type="number" class="form-control" id="endDistanceMeters"
                       v-model="currentDistancePricing.endDistanceMeters" min="0" required
                       :disabled="currentDistancePricing.isOpenEnded">
                <small class="text-muted">{{ (currentDistancePricing.endDistanceMeters / 1000).toFixed(2) }} km</small>
                <div class="invalid-feedback">Please enter a valid end distance.</div>
              </div>
            </div>

            <div class="row mb-3">
              <div class="col-md-6">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" id="isStartingRange"
                         v-model="currentDistancePricing.isStartingRange">
                  <label class="form-check-label" for="isStartingRange">
                    Starting Range (0 meters)
                  </label>
                </div>
                <small class="text-muted">Mark this as the first distance range (starts at 0)</small>
              </div>
              <div class="col-md-6">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" id="isOpenEnded"
                         v-model="currentDistancePricing.isOpenEnded">
                  <label class="form-check-label" for="isOpenEnded">
                    Open-Ended Range
                  </label>
                </div>
                <small class="text-muted">Mark this as an open-ended range (no upper limit)</small>
              </div>
            </div>

            <div class="row mb-3">
              <div class="col-md-6">
                <label for="rateType" class="form-label">Rate Type</label>
                <select id="rateType" class="form-select" v-model="currentDistancePricing.rateType" required>
                  <option value="">Select Rate Type</option>
                  <option value="FLAT">Flat</option>
                  <option value="PER_METER">Per Meter</option>
                  <option value="PER_KILOMETER">Per Kilometer</option>
                </select>
                <div class="invalid-feedback">Please select a rate type.</div>
              </div>
              <div class="col-md-6">
                <label for="rateAmount" class="form-label">Rate Amount</label>
                <div class="input-group">
                  <span class="input-group-text">$</span>
                  <input type="number" class="form-control" id="rateAmount" v-model="currentDistancePricing.rateAmount"
                         min="0" step="0.01" required>
                </div>
                <div class="invalid-feedback">Please enter a valid rate amount.</div>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button class="btn btn-danger" type="button" data-bs-dismiss="modal">Close</button>
          <button class="btn btn-primary" type="button" @click="saveDistancePricing">
            {{ distancePricingModalEditMode ? 'Update' : 'Add' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Geo Zone Modal -->
  <div class="modal fade show" id="geoZoneModal" tabindex="-1" role="dialog" aria-labelledby="geoZoneModal"
       aria-modal="true" style="display: none;">
    <div class="modal-dialog modal-dialog-centered modal-xl" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h3 class="modal-title fs-5">{{ geoZoneModalEditMode ? 'Update' : 'Add' }} Geo Zone Pricing</h3>
          <button class="btn-close" type="button" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body" v-if="showGeoZoneModal">
          <form>
            <div class="mb-3">
              <label for="geoZone" class="form-label">Geo Zone</label>
              <div class="d-block">
                <input class="form-control" name="geoZone" placeholder="Search geo zone"
                       ref="tagifyGeoZoneRef"/>
              </div>
              <div class="invalid-feedback">Please select a geo zone.</div>
            </div>

            <div class="mb-3">
              <label for="applicationType" class="form-label">Application Type</label>
              <select id="applicationType" class="form-select" v-model="currentGeoZone.applicationType" required>
                <option value="">Select Application Type</option>
                <option value="PICKUP">Pickup</option>
                <option value="DROPOFF">Dropoff</option>
                <option value="BOTH">Both</option>
                <option value="CROSSING">Crossing</option>
              </select>
              <div class="invalid-feedback">Please select an application type.</div>
            </div>

            <div class="mb-3">
              <label for="zoneRateType" class="form-label">Zone Rate Type</label>
              <select id="zoneRateType" class="form-select" v-model="currentGeoZone.zoneRateType" required>
                <option value="SURCHARGE">Fixed Surcharge — adds a flat amount to the fare</option>
                <option value="PERCENTAGE">Percentage — adds a % of base + distance fare</option>
                <option value="FLAT_FARE">Flat Fare — replaces the distance fare entirely</option>
                <option value="PER_KM">Per KM — overrides the global per-km distance rate</option>
              </select>
              <div class="invalid-feedback">Please select a zone rate type.</div>
            </div>

            <div class="row mb-3">
              <div class="col-md-6" v-if="currentGeoZone.zoneRateType === 'SURCHARGE'">
                <label for="surcharge" class="form-label">Surcharge ({{ fareConfig.state.currency }})</label>
                <div class="input-group">
                  <span class="input-group-text">$</span>
                  <input type="number" class="form-control" id="surcharge" v-model="currentGeoZone.surcharge" min="0"
                         step="0.01" required>
                </div>
                <small class="text-muted">Flat amount added to every matching ride</small>
                <div class="invalid-feedback">Please enter a valid surcharge (0 or more).</div>
              </div>

              <div class="col-md-6" v-if="currentGeoZone.zoneRateType === 'PERCENTAGE'">
                <label for="percentage" class="form-label">Percentage (%)</label>
                <div class="input-group">
                  <input type="number" class="form-control" id="percentage" v-model="currentGeoZone.percentage" min="0"
                         step="0.01" required>
                  <span class="input-group-text">%</span>
                </div>
                <small class="text-muted">Percentage of base + distance fare added to ride total</small>
                <div class="invalid-feedback">Please enter a valid percentage (0 or more).</div>
              </div>

              <div class="col-md-6" v-if="currentGeoZone.zoneRateType === 'FLAT_FARE'">
                <label for="flatFare" class="form-label">Flat Fare ({{ fareConfig.state.currency }})</label>
                <div class="input-group">
                  <span class="input-group-text">$</span>
                  <input type="number" class="form-control" id="flatFare" v-model="currentGeoZone.flatFare" min="0"
                         step="0.01" required>
                </div>
                <small class="text-muted">Fixed fare that replaces the distance fare for this zone</small>
                <div class="invalid-feedback">Please enter a valid flat fare (0 or more).</div>
              </div>

              <div class="col-md-6" v-if="currentGeoZone.zoneRateType === 'PER_KM'">
                <label for="zonePerKmRate" class="form-label">Per KM Rate ({{ fareConfig.state.currency }})</label>
                <div class="input-group">
                  <span class="input-group-text">$</span>
                  <input type="number" class="form-control" id="zonePerKmRate" v-model="currentGeoZone.zonePerKmRate"
                         min="0" step="0.01" required>
                  <span class="input-group-text">/km</span>
                </div>
                <small class="text-muted">Per-km rate that overrides the global distance config for this zone</small>
                <div class="invalid-feedback">Please enter a valid per-km rate (0 or more).</div>
              </div>

              <div class="col-md-6">
                <label for="fareMaximum" class="form-label">Fare Maximum / Cap ({{ fareConfig.state.currency }})</label>
                <div class="input-group">
                  <span class="input-group-text">$</span>
                  <input type="number" class="form-control" id="fareMaximum" v-model="currentGeoZone.fareMaximum"
                         min="0" step="0.01" placeholder="Optional">
                </div>
                <small class="text-muted">Maximum total fare for rides in this zone (leave blank for no cap)</small>
                <div class="invalid-feedback">Please enter a valid fare maximum (0 or more).</div>
              </div>
            </div>

            <div class="row mb-3">
              <div class="col-md-6">
                <label for="fareMaximumUpToMeters" class="form-label">Cap Applies Up To (meters)</label>
                <input type="number" class="form-control" id="fareMaximumUpToMeters"
                       v-model="currentGeoZone.fareMaximumUpToMeters" min="0" step="1" placeholder="Optional">
                <small class="text-muted">Cap only applies for trips shorter than this distance (leave blank = always applies)</small>
                <div class="invalid-feedback">Please enter a valid distance (0 or more).</div>
              </div>
            </div>

            <div class="row mb-3">
              <div class="col-md-6">
                <label for="fareMinimum" class="form-label">Fare Minimum ({{ fareConfig.state.currency }})</label>
                <div class="input-group">
                  <span class="input-group-text">$</span>
                  <input type="number" class="form-control" id="fareMinimum" v-model="currentGeoZone.fareMinimum"
                         min="0" step="0.01" placeholder="Optional">
                </div>
                <small class="text-muted">Minimum fare for rides in this zone (leave blank for no minimum)</small>
                <div class="invalid-feedback">Please enter a valid fare minimum (0 or more).</div>
              </div>
              <div class="col-md-6">
                <label for="fareMinimumUpToMeters" class="form-label">Minimum Applies Up To (meters)</label>
                <input type="number" class="form-control" id="fareMinimumUpToMeters"
                       v-model="currentGeoZone.fareMinimumUpToMeters" min="0" step="1" placeholder="Optional">
                <small class="text-muted">Minimum only applies for trips shorter than this distance (leave blank = always applies)</small>
                <div class="invalid-feedback">Please enter a valid distance (0 or more).</div>
              </div>
            </div>

            <hr>
            <p class="mb-2 fw-semibold">Platform fee for this zone (Asher keeps this)</p>
            <p class="text-muted small">
              Everything above adds to the fare, and the driver is paid the fare less commission — so
              the driver keeps most of it. The two amounts below go to Asher instead, for costs the
              platform carries such as insured kilometres on an empty return leg. They apply whichever
              rate type is selected above, so one zone can pay the driver a surcharge and charge Asher's
              side at the same time.
            </p>
            <div class="row mb-3">
              <div class="col-md-6">
                <label for="zonePlatformFee" class="form-label">Zone Platform Fee — Asher ({{ fareConfig.state.currency }})</label>
                <div class="input-group">
                  <span class="input-group-text">$</span>
                  <input type="number" class="form-control" id="zonePlatformFee"
                         v-model="currentGeoZone.zonePlatformFee" min="0" step="0.01" placeholder="Optional">
                </div>
                <small class="text-muted">Flat amount added to the platform fee for matching rides — not paid to the driver</small>
                <div class="invalid-feedback">Please enter a valid zone platform fee (0 or more).</div>
              </div>
              <div class="col-md-6">
                <label for="zonePlatformFeePerKm" class="form-label">Zone Platform Fee per km — Asher ({{ fareConfig.state.currency }})</label>
                <div class="input-group">
                  <span class="input-group-text">$</span>
                  <input type="number" class="form-control" id="zonePlatformFeePerKm"
                         v-model="currentGeoZone.zonePlatformFeePerKm" min="0" step="0.01" placeholder="Optional">
                  <span class="input-group-text">/km</span>
                </div>
                <small class="text-muted">Added to the platform fee per kilometre of the trip — not paid to the driver</small>
                <div class="invalid-feedback">Please enter a valid per-km zone platform fee (0 or more).</div>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button class="btn btn-danger" type="button" data-bs-dismiss="modal">Close</button>
          <button class="btn btn-primary" type="button" @click="saveGeoZone">
            {{ geoZoneModalEditMode ? 'Update' : 'Add' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {nextTick, ref, onMounted, watch, onUnmounted} from 'vue';
import DataTable from 'datatables.net-vue3';
import DataTablesCore from 'datatables.net-bs5';
import {Model} from "../modelTemplates/modelTemplates";
import {PLATFORM_FEE_PER_KM_FLOOR} from "../globals.js";
import {notificationType, showToast} from "../utility/toastNotificationUtility";
import {activeBadgeRender, booleanBadgeRender, editAndDeleteCTA} from "../utility/DatatableRenderer";
import Swal from "../../extras/js/sweetalert/sweetalert2.min";
import Tagify from '../../extras/js/vendors/@yaireo/tagify/dist/tagify';

DataTable.use(DataTablesCore);

const props = defineProps({
  serviceProviderId: {type: String, default: null},
  serviceJurisdictionId: {type: String, default: null}
});

const fareConfig = ref(Model("FareConfiguration").call({}));

const platformFeePerKmFloor = PLATFORM_FEE_PER_KM_FLOOR;

/**
 * Reads a numeric form field for the save payload, treating a cleared input as unset.
 *
 * A cleared `<input type="number">` yields '' rather than null or undefined, which `?? fallback` does
 * not catch — posting '' for the per-km rate would deserialise to NULL server-side and silently
 * switch the distance-scaled fee off.
 */
const numericFieldOrDefault = (value, fallback) =>
    (value === '' || value === null || value === undefined) ? fallback : value;

const getApiBaseUrl = () => {
  if (props.serviceJurisdictionId) {
    return `/api/service-jurisdiction/${props.serviceJurisdictionId}`;
  }
  return `/api/service-provider/${props.serviceProviderId}`;
};

//fetch configurations
const response = await fetch(`${getApiBaseUrl()}/fare-configuration`);
if (!response.ok) {
  showToast({
    title: 'Error',
    body: `Encountered an error loading fare configuration.`,
    type: notificationType.ERROR,
    delay: 7000
  });
}
const data = await response.json();
//populate fareConfig with the fetched data
Object.keys(data).forEach((key) => {
  if (key in fareConfig.value.state) {
    fareConfig.value.state[key] = data[key];
  }
});


/*
* Base price set up
* */

const basePriceModalEditMode = ref(false);
const showBasePriceModal = ref(false);
const currentBasePrice = ref({
  vehicleType: '',
  baseFare: 0.00
});
const currentBasePriceIndex = ref(-1);

const toggleCreateBasePriceModal = (editMode, row, index) => {
  basePriceModalEditMode.value = editMode || false;

  if (editMode && row) {
    // Clone the row data to avoid direct mutation
    currentBasePrice.value = JSON.parse(JSON.stringify(row));
    currentBasePriceIndex.value = index;
  } else {
    // Reset form for new entry
    currentBasePrice.value = {
      vehicleType: '',
      baseFare: 0.00
    };
    currentBasePriceIndex.value = -1;
  }

  $('#createBasePricingModal').modal('toggle');
  showBasePriceModal.value = false;
  nextTick(() => {
    showBasePriceModal.value = true;
  });
};

const validateBasePriceForm = () => {
  // Use the validation method from the model
  const validationResult = fareConfig.value.validateVehicleBaseFareConfig(currentBasePrice.value);

  if (!validationResult.isValid) {
    showToast({
      title: 'Validation Error',
      body: validationResult.message,
      type: notificationType.ERROR
    });
    return false;
  }

  return true;
};

const saveBasePrice = () => {
  // Validate form data
  if (!validateBasePriceForm()) {
    return;
  }

  if (!basePriceModalEditMode.value) {
    // Check for duplicate vehicle type using the model's method
    const duplicateCheck = fareConfig.value.checkDuplicateVehicleType(currentBasePrice.value.vehicleType);

    if (duplicateCheck.isDuplicate) {
      showToast({
        title: 'Validation Error',
        body: duplicateCheck.message,
        type: notificationType.ERROR
      });
      return;
    }

    // Add new base price
    fareConfig.value.addVehicleBaseFareConfig();
    const newIndex = fareConfig.value.state.vehicleBaseFareConfigs.length - 1;
    fareConfig.value.state.vehicleBaseFareConfigs[newIndex] = {...currentBasePrice.value};

  } else {
    // Check for duplicate vehicle type when updating using the model's method
    const duplicateCheck = fareConfig.value.checkDuplicateVehicleType(
        currentBasePrice.value.vehicleType,
        currentBasePriceIndex.value
    );

    if (duplicateCheck.isDuplicate) {
      showToast({
        title: 'Validation Error',
        body: duplicateCheck.message,
        type: notificationType.ERROR
      });
      return;
    }

    // Update existing base price
    fareConfig.value.state.vehicleBaseFareConfigs[currentBasePriceIndex.value] = {...currentBasePrice.value};
    showToast({
      title: 'Success',
      body: `Base price for ${currentBasePrice.value.vehicleType} has been updated.`,
      type: notificationType.SUCCESS
    });
  }

  // Close the modal
  $('#createBasePricingModal').modal('hide');
};

const editBasePrice = (row, meta) => {
  const index = meta.row;
  toggleCreateBasePriceModal(true, row, index);
};

const deleteBasePrice = (row, meta) => {
  Swal.fire({
    text: `Are you sure you want to delete the base price for ${row.vehicleType}?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6'
  }).then((result) => {
    if (result.isConfirmed) {
      // Find the index of the row in the vehicleBaseFareConfig array
      const index = meta.row;
      // Remove the base price from the list
      fareConfig.value.removeVehicleBaseFareConfig(index);
    }
  });
};

/*
* Distance Range setup
* */
const distancePricingModalEditMode = ref(false);
const showDistancePricingModal = ref(false);
const currentDistancePricing = ref({
  startDistanceMeters: 0,
  endDistanceMeters: 0,
  isStartingRange: false,
  isOpenEnded: false,
  rateType: '',
  rateAmount: 0.00
});
const currentDistancePricingIndex = ref(-1);

const toggleDistancePricingModal = (editMode, row, index) => {
  distancePricingModalEditMode.value = editMode || false;

  if (editMode && row) {
    // Clone the row data to avoid direct mutation
    currentDistancePricing.value = JSON.parse(JSON.stringify(row));
    currentDistancePricingIndex.value = index;
  } else {
    // Reset form for new entry
    currentDistancePricing.value = {
      startDistanceMeters: 0,
      endDistanceMeters: 0,
      isStartingRange: false,
      isOpenEnded: false,
      rateType: '',
      rateAmount: 0.00
    };
    currentDistancePricingIndex.value = -1;
  }

  $('#distancePricingModal').modal('toggle');
  showDistancePricingModal.value = false;
  nextTick(() => {
    showDistancePricingModal.value = true;
  });
};

// Watch for changes to isStartingRange
watch(() => currentDistancePricing.value.isStartingRange, (newValue) => {
  if (newValue) {
    // If this is a starting range, set startDistanceMeters to 0
    currentDistancePricing.value.startDistanceMeters = 0;
  }
});

// Watch for changes to isOpenEnded
watch(() => currentDistancePricing.value.isOpenEnded, (newValue) => {
  if (newValue) {
    // If this is an open-ended range, set a high default value for endDistanceMeters
    currentDistancePricing.value.endDistanceMeters = 1000000; // 1000 km
  }
});

const validateDistancePricingForm = () => {
  // Use the validation method from the model
  const validationResult = fareConfig.value.validateDistancePricing(currentDistancePricing.value);

  if (!validationResult.isValid) {
    showToast({
      title: 'Validation Error',
      body: validationResult.message,
      type: notificationType.ERROR
    });
    return false;
  }

  return true;
};

const saveDistancePricing = () => {
  // Validate form data
  if (!validateDistancePricingForm()) {
    return;
  }

  if (!distancePricingModalEditMode.value) {
    // Check for overlapping distance ranges using the model's method
    const overlapCheck = fareConfig.value.checkOverlappingDistanceFareConfig(currentDistancePricing.value);

    if (overlapCheck.isOverlapping) {
      showToast({
        title: 'Validation Error',
        body: overlapCheck.message,
        type: notificationType.ERROR
      });
      return;
    }

    // Add new distance range
    fareConfig.value.addDistanceFareConfig();
    const newIndex = fareConfig.value.state.distanceFareConfigs.length - 1;
    fareConfig.value.state.distanceFareConfigs[newIndex] = {...currentDistancePricing.value};
  } else {
    // Check for overlapping distance ranges when updating using the model's method
    const overlapCheck = fareConfig.value.checkOverlappingDistanceFareConfig(
        currentDistancePricing.value,
        currentDistancePricingIndex.value
    );

    if (overlapCheck.isOverlapping) {
      showToast({
        title: 'Validation Error',
        body: overlapCheck.message,
        type: notificationType.ERROR
      });
      return;
    }

    // Update existing distance range
    fareConfig.value.state.distanceFareConfigs[currentDistancePricingIndex.value] = {...currentDistancePricing.value};
    showToast({
      title: 'Success',
      body: `Distance range ${currentDistancePricing.value.startDistanceMeters}-${currentDistancePricing.value.endDistanceMeters} meters has been updated.`,
      type: notificationType.SUCCESS
    });
  }

  // Close the modal
  $('#distancePricingModal').modal('hide');
};

const editDistancePricing = (row, meta) => {
  // Find the index of the row in the distancePricings array
  const index = meta.row;
  toggleDistancePricingModal(true, row, index);
};

const deleteDistancePricing = (row, meta) => {
  Swal.fire({
    title: 'Delete Distance Range',
    text: `Are you sure you want to delete the distance range ${row.startDistanceMeters}-${row.endDistanceMeters} meters?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#3085d6',
    cancelButtonColor: '#d33'
  }).then((result) => {
    if (result.isConfirmed) {
      // Find the index of the row in the distanceFareConfig array
      const index = meta.row;

      // Remove the distance range from the list
      fareConfig.value.removeDistanceFareConfig(index);

      showToast({
        title: 'Success',
        body: `Distance range ${row.startDistanceMeters}-${row.endDistanceMeters} meters has been deleted.`,
        type: notificationType.SUCCESS
      });
    }
  });
};

/*
* Geo Zone setup
* */
const geoZoneModalEditMode = ref(false);
const showGeoZoneModal = ref(false);
const currentGeoZone = ref({
  geoZone: null,
  applicationType: '',
  zoneRateType: 'SURCHARGE',
  surcharge: 0.00,
  percentage: 0.00,
  flatFare: 0.00,
  zonePerKmRate: 0.00,
  fareMaximum: null,
  fareMaximumUpToMeters: null,
  fareMinimum: null,
  fareMinimumUpToMeters: null,
  zonePlatformFee: null,
  zonePlatformFeePerKm: null,
});
const currentGeoZoneIndex = ref(-1);

// Tagify related variables
const tagifyGeoZoneRef = ref(null);
const tagifyGeoZoneInstance = ref(null);
const tagifyGeoZoneList = ref([]);

// Fetch available geo zones
const fetchGeoZones = async () => {
  try {
    const response = await fetch('/api/fare-zones');
    if (response.ok) {
      const data = await response.json();
      tagifyGeoZoneList.value = data.map(zone => ({
        id: zone.id,
        value: zone.name,
        name: zone.name,
        description: zone.description,
        zoneType: zone.zoneType
      }));
    } else {
      console.error('Failed to fetch geo zones:', response.status);
    }
  } catch (error) {
    console.error('Error fetching geo zones:', error);
    Swal.fire({
      title: 'Error',
      text: 'Failed to load geo zones',
      icon: 'error'
    });
  }
};

// Initialize tagify for geo zone selection
const initializeGeoZoneTagify = async () => {
  await nextTick();
  // If Tagify is already initialized, destroy it first
  if (tagifyGeoZoneInstance.value) {
    tagifyGeoZoneInstance.value.destroy();
    tagifyGeoZoneInstance.value = null;
  }

  // Initialize Tagify on the geo zone input
  tagifyGeoZoneInstance.value = new Tagify(tagifyGeoZoneRef.value, {
    maxTags: 1,
    enforceWhitelist: true,
    whitelist: tagifyGeoZoneList.value,
    dropdown: {
      closeOnSelect: true,
      highlightFirst: true,
      enabled: 0,
      classname: "extra-properties", // custom class for the suggestions dropdown
    },
    templates: {
      tag: function (tagData) {
        try {
          return `<tag title='${tagData.value}' contenteditable='false' spellcheck="false" class='tagify__tag ${tagData.class ? tagData.class : ""}' ${this.getAttributes(tagData)}>
                      <x title='remove tag' class='tagify__tag__removeBtn'></x>
                      <div>
                          <span class='tagify__tag-text'>${tagData.value}</span>
                      </div>
                  </tag>`;
        } catch (err) {
          console.error(err);
        }
      },
      dropdownItem: function (tagData) {
        try {
          return `<div ${this.getAttributes(tagData)} class='tagify__dropdown__item ${tagData.class ? tagData.class : ""}'>
                    <span>${tagData.value}</span>
                  </div>`;
        } catch (err) {
          console.error(err);
        }
      },
    },
  });

  // Set initial value if currentGeoZone has a geoZone
  if (currentGeoZone.value?.geoZone) {
    const foundZone = tagifyGeoZoneList.value.find(z => z.id === currentGeoZone.value.geoZone.id);
    if (foundZone) {
      tagifyGeoZoneInstance.value.addTags([{
        value: foundZone.name.toString(),
        name: foundZone.name,
        id: foundZone.id,
        description: foundZone.description,
        zoneType: foundZone.zoneType
      }]);
    }
  }

  // Update currentGeoZone.geoZone when tag changes
  tagifyGeoZoneInstance.value.on("remove", updateSelectedGeoZone);
  tagifyGeoZoneInstance.value.on("add", updateSelectedGeoZone);
};

// Update currentGeoZone.geoZone when tag changes
const updateSelectedGeoZone = () => {
  if (!tagifyGeoZoneInstance.value) return;
  try {
    const tags = tagifyGeoZoneInstance.value.value || [];
    if (tags.length > 0) {
      const tag = tags[0]; // Only one tag is allowed
      const zone = tagifyGeoZoneList.value.find(z => z.value.toString() === tag.value.toString());
      if (zone) {
        currentGeoZone.value.geoZone = {
          id: zone.id,
          name: zone.name,
          description: zone.description,
          zoneType: zone.zoneType
        };
      }
    } else {
      currentGeoZone.value.geoZone = null;
    }
  } catch (error) {
    console.error('Error updating selected geo zone:', error);
  }
};

const toggleGeoZoneModal = (editMode, row, index) => {
  geoZoneModalEditMode.value = editMode || false;

  if (editMode && row) {
    // Clone the row data to avoid direct mutation; normalise legacy isPercentage rows
    const cloned = JSON.parse(JSON.stringify(row));
    if (!cloned.zoneRateType) {
      cloned.zoneRateType = cloned.isPercentage ? 'PERCENTAGE' : 'SURCHARGE';
    }
    if (cloned.flatFare === undefined) cloned.flatFare = 0.00;
    if (cloned.zonePerKmRate === undefined) cloned.zonePerKmRate = 0.00;
    if (cloned.fareMaximum === undefined) cloned.fareMaximum = null;
    if (cloned.fareMaximumUpToMeters === undefined) cloned.fareMaximumUpToMeters = null;
    if (cloned.fareMinimum === undefined) cloned.fareMinimum = null;
    if (cloned.fareMinimumUpToMeters === undefined) cloned.fareMinimumUpToMeters = null;
    // Backfill, not default: a zone loaded from a server response that predates these fields would
    // otherwise come back undefined, drop out of the save payload, and lose its stored fee the next
    // time an admin edited anything else on the zone.
    if (cloned.zonePlatformFee === undefined) cloned.zonePlatformFee = null;
    if (cloned.zonePlatformFeePerKm === undefined) cloned.zonePlatformFeePerKm = null;
    currentGeoZone.value = cloned;
    currentGeoZoneIndex.value = index;
  } else {
    // Reset form for new entry
    currentGeoZone.value = {
      geoZone: null,
      applicationType: '',
      zoneRateType: 'SURCHARGE',
      surcharge: 0.00,
      percentage: 0.00,
      flatFare: 0.00,
      zonePerKmRate: 0.00,
      fareMaximum: null,
      fareMaximumUpToMeters: null,
      fareMinimum: null,
      fareMinimumUpToMeters: null,
      zonePlatformFee: null,
      zonePlatformFeePerKm: null,
    };
    currentGeoZoneIndex.value = -1;
  }

  $('#geoZoneModal').modal('toggle');
  showGeoZoneModal.value = false;
  nextTick(() => {
    showGeoZoneModal.value = true;
    // Initialize tagify after the modal is shown
    initializeGeoZoneTagify();
  });
};

const validateGeoZoneForm = () => {
  // Use the validation method from the model
  const validationResult = fareConfig.value.validateGeoZone(currentGeoZone.value);

  if (!validationResult.isValid) {
    showToast({
      title: 'Validation Error',
      body: validationResult.message,
      type: notificationType.ERROR
    });
    return false;
  }

  return true;
};

const saveGeoZone = () => {
  // Validate form data
  if (!validateGeoZoneForm()) {
    return;
  }

  if (!geoZoneModalEditMode.value) {
    // Check for duplicate geo zone and application type using the model's method
    const duplicateCheck = fareConfig.value.checkDuplicateGeoZone(currentGeoZone.value);

    if (duplicateCheck.isDuplicate) {
      showToast({
        title: 'Validation Error',
        body: duplicateCheck.message,
        type: notificationType.ERROR
      });
      return;
    }

    // Add new geo zone
    fareConfig.value.addGeoZoneFareConfig();
    const newIndex = fareConfig.value.state.geoZoneFareConfigs.length - 1;
    fareConfig.value.state.geoZoneFareConfigs[newIndex] = {...currentGeoZone.value};
  } else {
    // Check for duplicate geo zone and application type when updating using the model's method
    const duplicateCheck = fareConfig.value.checkDuplicateGeoZone(
        currentGeoZone.value,
        currentGeoZoneIndex.value
    );

    if (duplicateCheck.isDuplicate) {
      showToast({
        title: 'Validation Error',
        body: duplicateCheck.message,
        type: notificationType.ERROR
      });
      return;
    }

    // Update existing geo zone
    fareConfig.value.state.geoZoneFareConfigs[currentGeoZoneIndex.value] = {...currentGeoZone.value};
    showToast({
      title: 'Success',
      body: `Geo zone pricing has been updated.`,
      type: notificationType.SUCCESS
    });
  }

  // Close the modal
  $('#geoZoneModal').modal('hide');
};

const editGeoZone = (row, meta) => {
  // Find the index of the row in the geoZones array
  const index = meta.row;
  toggleGeoZoneModal(true, row, index);
};

const deleteGeoZone = (row, meta) => {
  Swal.fire({
    title: 'Delete Geo Zone Pricing',
    text: `Are you sure you want to delete this geo zone pricing?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#3085d6',
    cancelButtonColor: '#d33'
  }).then((result) => {
    if (result.isConfirmed) {
      // Find the index of the row in the geoZoneFareConfig array
      const index = meta.row;

      // Remove the geo zone from the list
      fareConfig.value.removeGeoZoneFareConfig(index);

      showToast({
        title: 'Success',
        body: `Geo zone pricing has been deleted.`,
        type: notificationType.SUCCESS
      });
    }
  });
};


/*
* DataTable Columns Setup
* */
const basePricesColumns = [
  {data: 'vehicleType', title: 'Vehicle Type'},
  {data: 'baseFare', title: `Base Fare`, render: data => `$${parseFloat(data).toFixed(2)}`},
  {
    data: null,
    title: 'Actions',
    orderable: false,
    render: (data, type, row, meta) => {
      return editAndDeleteCTA(data, type, row, meta, editBasePrice, deleteBasePrice,);
    }
  }
];


const distancePricingsColumns = [
  {data: 'rangeDescription', title: 'Range'},
  {
    data: 'isStartingRange',
    title: 'Starting Range',
    render: booleanBadgeRender
  },
  {
    data: 'isOpenEnded',
    title: 'Open-Ended',
    render: booleanBadgeRender
  },
  {data: 'rateType', title: 'Rate Type'},
  {data: 'rateAmount', title: `Rate Amount `, render: data => `$${parseFloat(data).toFixed(2)}`},
  {
    data: null,
    title: 'Actions',
    orderable: false,
    render: (data, type, row, meta) => {
      return editAndDeleteCTA(data, type, row, meta, editDistancePricing, deleteDistancePricing);
    }
  }
];

const formatGeoZoneRate = (row) => {
  const rateType = row.zoneRateType || (row.isPercentage ? 'PERCENTAGE' : 'SURCHARGE');
  switch (rateType) {
    case 'FLAT_FARE':
      return `$${parseFloat(row.flatFare || 0).toFixed(2)} flat`;
    case 'PER_KM':
      return `$${parseFloat(row.zonePerKmRate || 0).toFixed(2)}/km`;
    case 'PERCENTAGE':
      return `+${parseFloat(row.percentage || 0).toFixed(2)}%`;
    case 'SURCHARGE':
    default:
      return `+$${parseFloat(row.surcharge || 0).toFixed(2)}`;
  }
};

// The Asher-side fee is invisible in the Rate column above, which only describes the rider side.
// Without its own column an airport rule reads as "+$2.50" in the table while charging $4.50.
const formatZonePlatformFee = (row) => {
  const isSet = (value) => value !== null && value !== undefined && value !== '' && parseFloat(value) !== 0;
  const parts = [];
  if (isSet(row.zonePlatformFee)) {
    parts.push(`$${parseFloat(row.zonePlatformFee).toFixed(2)}`);
  }
  if (isSet(row.zonePlatformFeePerKm)) {
    parts.push(`$${parseFloat(row.zonePlatformFeePerKm).toFixed(2)}/km`);
  }
  return parts.length ? parts.join(' + ') : '—';
};

const geoZonesPricingColumns = [
  {data: 'geoZone.name', title: 'Zone Name'},
  {data: 'applicationType', title: 'Application Type'},
  {
    data: null,
    title: 'Rate',
    render: (data, type, row) => formatGeoZoneRate(row)
  },
  {
    data: 'fareMaximum',
    title: 'Cap',
    render: (data) => (data !== null && data !== undefined && data !== '') ? `$${parseFloat(data).toFixed(2)}` : '—'
  },
  {
    data: null,
    title: 'Asher Fee',
    render: (data, type, row) => formatZonePlatformFee(row)
  },
  {
    data: null,
    title: 'Actions',
    orderable: false,
    render: (data, type, row, meta) => {
      return editAndDeleteCTA(data, type, row, meta, editGeoZone, deleteGeoZone);
    }
  }
];


// Initialize everything when component is mounted
onMounted(async () => {
  await fetchGeoZones();
});

// Cleanup when component is unmounted
onUnmounted(() => {
  if (tagifyGeoZoneInstance.value) {
    tagifyGeoZoneInstance.value.destroy();
    tagifyGeoZoneInstance.value = null;
  }
});

// Save the entire fare configuration
const saveConfiguration = async () => {
  try {
    // Validate the configuration
    if (!fareConfig.value.isValid()) {
      showToast({
        title: 'Validation Error',
        body: 'Please fix the validation errors before saving.',
        type: notificationType.ERROR
      });
      return;
    }

    // A per-km fee below the policy floor is allowed but never accidental — confirm it explicitly.
    if (fareConfig.value.isPlatformFeePerKmBelowFloor()) {
      const confirmation = await Swal.fire({
        title: 'Per-km platform fee is below the floor',
        html: `A per-km fee under $${platformFeePerKmFloor.toFixed(2)}/km does not cover the ` +
            `insurance cost that scales with distance, so margin shrinks the longer the trip. ` +
            `Move the fare, not the fee. Save anyway?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Save anyway',
        cancelButtonText: 'Cancel'
      });
      if (!confirmation.isConfirmed) {
        return;
      }
    }

    // Show loading indicator
    Swal.fire({
      title: 'Saving...',
      text: 'Saving fare configuration',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    // Prepare the configuration data
    const configData = {
      id: fareConfig.value.state.id,
      currency: fareConfig.value.state.currency,
      freeStopWaitingMinutes: fareConfig.value.state.freeStopWaitingMinutes,
      perStopCharge: fareConfig.value.state.perStopCharge,
      stopPerMinuteCharge: fareConfig.value.state.stopPerMinuteCharge,
      platformFee: fareConfig.value.state.platformFee,
      platformFeePerKm: numericFieldOrDefault(fareConfig.value.state.platformFeePerKm, 0),
      platformFeeMinimum: numericFieldOrDefault(fareConfig.value.state.platformFeeMinimum, 0),
      platformFeeMaximum: numericFieldOrDefault(fareConfig.value.state.platformFeeMaximum, null),
      scheduledRideFee: fareConfig.value.state.scheduledRideFee,
      perMinuteFare: fareConfig.value.state.perMinuteFare,
      minimumFare: fareConfig.value.state.minimumFare,
      // Sent explicitly, like every other field here: the server rebuilds the row from the payload,
      // so a key that is merely absent lands on the Java default rather than on what is stored.
      minimumAppliesToFareOnly: fareConfig.value.state.minimumAppliesToFareOnly !== false,
      perAdditionalPassengerCharge: fareConfig.value.state.perAdditionalPassengerCharge,
      additionalPassengerMaxCharge: fareConfig.value.state.additionalPassengerMaxCharge,
      additionalPassengerFreeUnderAge: fareConfig.value.state.additionalPassengerFreeUnderAge ?? null,
      vehicleBaseFareConfigs: fareConfig.value.state.vehicleBaseFareConfigs,
      distanceFareConfigs: fareConfig.value.state.distanceFareConfigs,
      geoZoneFareConfigs: fareConfig.value.state.geoZoneFareConfigs.map(zone => {
        const effectiveRateType = zone.zoneRateType ?? (zone.isPercentage ? 'PERCENTAGE' : 'SURCHARGE');
        return {
          ...zone,
          zoneRateType: effectiveRateType,
          // Only the value field matching zoneRateType may be non-null -- the server's mutual-
          // exclusivity check (FareConfigurationService.validateGeoZoneRateType) rejects the save
          // otherwise. Switching the rate type dropdown only toggles which input is *shown*; it
          // doesn't clear the other three, so a zone edited more than once carries stale non-null
          // values (previously ?? 0 on flatFare/zonePerKmRate turned even untouched fields non-null).
          surcharge: effectiveRateType === 'SURCHARGE' ? numericFieldOrDefault(zone.surcharge, null) : null,
          percentage: effectiveRateType === 'PERCENTAGE' ? numericFieldOrDefault(zone.percentage, null) : null,
          flatFare: effectiveRateType === 'FLAT_FARE' ? numericFieldOrDefault(zone.flatFare, null) : null,
          zonePerKmRate: effectiveRateType === 'PER_KM' ? numericFieldOrDefault(zone.zonePerKmRate, null) : null,
          fareMaximum: zone.fareMaximum ?? null,
          fareMaximumUpToMeters: zone.fareMaximumUpToMeters ?? null,
          fareMinimum: zone.fareMinimum ?? null,
          fareMinimumUpToMeters: zone.fareMinimumUpToMeters ?? null,
          // Named explicitly rather than left to the spread: the server rebuilds each zone row from the
          // payload, so a key that is merely absent is written back as the Java field default and the
          // zone's Asher-side fee is silently zeroed by an edit to something else entirely.
          zonePlatformFee: numericFieldOrDefault(zone.zonePlatformFee, null),
          zonePlatformFeePerKm: numericFieldOrDefault(zone.zonePlatformFeePerKm, null),
        };
      }),
    };


    const url = `${getApiBaseUrl()}/fare-configuration/${fareConfig.value.state.id}`;

    // Send the request
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(configData)
    });

    // Close loading indicator
    Swal.close();

    if (!response.ok) {
      const errorData = await response.json();
      showToast({
        title: 'Error',
        body: `Failed to save fare configuration: ${errorData.message || response.statusText}`,
        type: notificationType.ERROR,
        delay: 7000
      });
      return;
    }

    // Update the local state with the response data
    const savedConfig = await response.json();
    Object.keys(savedConfig).forEach((key) => {
      if (key in fareConfig.value.state) {
        fareConfig.value.state[key] = savedConfig[key];
      }
    });

    showToast({
      title: 'Success',
      body: 'Fare configuration saved successfully.',
      type: notificationType.SUCCESS
    });
  } catch (error) {
    console.error('Error saving fare configuration:', error);
    showToast({
      title: 'Error',
      body: `An unexpected error occurred: ${error.message}`,
      type: notificationType.ERROR,
      delay: 7000
    });
  }
};
</script>
